import * as Updates from 'expo-updates';
import { createMMKV } from 'react-native-mmkv';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Records the intent to restart into a freshly downloaded update.
 */
export function persistRestartIntent() {
  const fromUpdateId = Updates.updateId;

  if (!fromUpdateId) return;

  STORAGE.set(RESTART_FROM_UPDATE_ID_KEY, fromUpdateId);
}

/**
 * Classifies the current launch against the bundle recorded at the previous
 * launch and against any restart we requested but never got to confirm.
 *
 * Consumes the persisted state, so it is expected to run once per launch.
 */
export function reconcileLaunch(): LaunchOutcome {
  const restartFromUpdateId = STORAGE.getString(RESTART_FROM_UPDATE_ID_KEY);
  const previousUpdateId = STORAGE.getString(LAST_UPDATE_ID_KEY);

  STORAGE.remove(RESTART_FROM_UPDATE_ID_KEY);

  if (Updates.updateId) {
    STORAGE.set(LAST_UPDATE_ID_KEY, Updates.updateId);
  }

  return {
    kind: getLaunchKind({ restartFromUpdateId, previousUpdateId }),
    previousUpdateId,
    currentUpdateId: Updates.updateId,
    runtimeVersion: Updates.runtimeVersion,
    isEmergencyLaunch: Updates.isEmergencyLaunch,
    trigger: restartFromUpdateId ? 'in-app' : 'passive',
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getLaunchKind(params: Params): LaunchOutcome['kind'] {
  const { restartFromUpdateId, previousUpdateId } = params;

  // We asked for a restart and came back on the same bundle: the update never
  // took, and the device would otherwise keep serving the old one silently.
  if (restartFromUpdateId && Updates.updateId === restartFromUpdateId) {
    return 'install-failed';
  }

  // Nothing to compare against on the first launch after a native installation.
  if (previousUpdateId === undefined) {
    return 'baseline';
  }

  if (Updates.updateId === previousUpdateId) {
    return 'unchanged';
  }

  return 'update-applied';
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STORAGE = createMMKV({ id: 'sg.ota' });

const RESTART_FROM_UPDATE_ID_KEY = 'restart-from-update-id';
const LAST_UPDATE_ID_KEY = 'last-update-id';

// ─── Types ───────────────────────────────────────────────────────────────────

export type LaunchOutcome = {
  trigger: 'in-app' | 'passive';
  kind: 'baseline' | 'unchanged' | 'update-applied' | 'install-failed';
  previousUpdateId: string | undefined;
  currentUpdateId: string | null;
  runtimeVersion: string | null;
  isEmergencyLaunch: boolean;
};

type Params = {
  restartFromUpdateId: string | undefined;
  previousUpdateId: string | undefined;
};
